/**
 * The three plan cards (build Appendix A3): Research, Host, Property Manager.
 * Used by /pricing and the homepage pricing section. Every number comes from
 * `@shared/pricing` via `pricing-copy`.
 */
import { Check } from "lucide-react";
import { StartFreeButton } from "@/components/marketing/cta";
import {
  FREE_RESEARCH_SEARCHES,
  HOST_BASE_PRICE,
  OWNER_PRICE,
  TIER_STEPS_LINE,
  TIER_STEPS_SHORT,
  TRIAL_DAYS,
} from "@/lib/marketing/pricing-copy";

/** Host card bullets — also reused by /syre ("What's included: everything in Host"). */
export const HOST_PLAN_BULLETS = [
  "Team inbox synced with PadSplit; AI-drafted replies require beta access",
  "Maintenance: service forms, tasks, vendors, photo proof, supply orders",
  "Smart locks (TTLock and Sifely) and thermostats",
  "Leasing: booking requests, your own booking forms, RapidFill pricing, direct-booking site",
  "Live P&L for every property",
  "Unlimited users and unlimited research",
  "3 daily buy box agents: new houses that fit, every morning",
];

export const RESEARCH_PLAN_BULLETS = [
  `${FREE_RESEARCH_SEARCHES} buy box searches a month`,
  "Market research and underwriting with PDF export",
  "Lender and realtor directories",
  "Savings calculator",
];

export const PM_PLAN_BULLETS = [
  "Everything in Host",
  "Owner portal with monthly owner statements",
];

function Bullets({ items, testId }: { items: string[]; testId: string }) {
  return (
    <ul className="mt-6 space-y-3 text-sm text-gray-700 text-left" data-testid={testId}>
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <Check size={16} className="mt-0.5 shrink-0 text-emerald-600" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function PricingCards({ location }: { location: string }) {
  const trialLabel = `Start ${TRIAL_DAYS}-day free trial`;
  return (
    <div className="grid gap-6 md:grid-cols-3 max-w-6xl mx-auto" data-testid="pricing-cards">
      {/* Research */}
      <div
        className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
        data-testid="card-plan-research"
      >
        <h3 className="text-xl font-bold text-gray-900">Research</h3>
        <p className="mt-4 text-4xl font-bold text-gray-900" data-testid="text-price-research">
          $0
        </p>
        <p className="mt-3 text-sm text-gray-600">For investors before their first co-living house.</p>
        <Bullets items={RESEARCH_PLAN_BULLETS} testId="list-research-bullets" />
        <div className="mt-auto pt-8">
          <StartFreeButton
            ctaId={`${location}_research`}
            location={location}
            label="Start free research"
            signupParams={{ role: "researcher" }}
            showArrow={false}
            size="default"
            className="w-full bg-white text-brand border border-brand/30 shadow-none hover:bg-brand/5 hover:text-brand"
          />
        </div>
      </div>

      {/* Host */}
      <div
        className="relative flex flex-col rounded-2xl border-2 border-brand-accent bg-white p-6 sm:p-8 shadow-xl"
        data-testid="card-plan-host"
      >
        <span
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-accent px-4 py-1 text-xs font-bold text-white shadow"
          data-testid="badge-most-hosts"
        >
          Most hosts
        </span>
        <h3 className="text-xl font-bold text-gray-900">Host</h3>
        <p className="mt-4 text-gray-900" data-testid="text-price-host">
          <span className="text-4xl font-bold">{HOST_BASE_PRICE}</span>
          <span className="text-base text-gray-600"> per property / month</span>
        </p>
        <p className="mt-2 text-sm text-gray-600" data-testid="text-host-tier-steps">
          {TIER_STEPS_LINE}
        </p>
        <Bullets items={HOST_PLAN_BULLETS} testId="list-host-bullets" />
        <p className="mt-4 text-sm text-gray-600">Continuous Auto Draft requires beta access and enabling the feature. Signup or a paid plan alone does not grant access. Auto-send is off by default; emergencies need a person, and quiet hours hold automatic sends.</p>
        <div className="mt-auto pt-8">
          <StartFreeButton
            ctaId={`${location}_host`}
            location={location}
            label={trialLabel}
            showArrow={false}
            size="default"
            className="w-full"
          />
          <p className="mt-3 text-center text-xs text-gray-500" data-testid="text-host-fine-print">
            No card to start. Cancel anytime.
          </p>
        </div>
      </div>

      {/* Property Manager */}
      <div
        className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
        data-testid="card-plan-property-manager"
      >
        <h3 className="text-xl font-bold text-gray-900">Property Manager</h3>
        <p className="mt-4 text-gray-900" data-testid="text-price-property-manager">
          <span className="text-4xl font-bold">{HOST_BASE_PRICE}</span>
          <span className="text-base text-gray-600"> per property / month, + {OWNER_PRICE} per owner / month</span>
        </p>
        <p className="mt-2 text-sm text-gray-600" data-testid="text-pm-tier-steps">
          Same property pricing as Host: {TIER_STEPS_SHORT}
        </p>
        <Bullets items={PM_PLAN_BULLETS} testId="list-pm-bullets" />
        <p className="mt-4 text-sm text-gray-600">AI-drafted replies require beta access; a paid plan alone does not grant access.</p>
        <div className="mt-auto pt-8">
          <StartFreeButton
            ctaId={`${location}_property_manager`}
            location={location}
            label={trialLabel}
            signupParams={{ role: "property_manager" }}
            showArrow={false}
            size="default"
            className="w-full bg-brand hover:bg-brand-hover"
          />
        </div>
      </div>
    </div>
  );
}
